import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import type { MetaAdsCampaignDraft } from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { parseError } from "../common/error";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export const useCampaignDrafts = () => {
	const company = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.Drafts(company.id),
		queryFn: () => Api.GetMetaAdsDrafts().then((r) => r.data),
	});
};

export const useCampaignDraft = (draftId: string) => {
	const company = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.Draft(company.id, draftId),
		queryFn: () => Api.GetMetaAdsDraft(draftId).then((r) => r.data),
	});
};

export const useCampaignDraftActions = () => {
	const company = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const saveDraft = useMutation({
		mutationFn: (draft: MetaAdsCampaignDraft) =>
			Api.SaveMetaAdsDraft(draft).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.Drafts(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Draft saved." });
		},
		onError: (error) => {
			const msg = parseError(error) ?? "Unknown error";
			emit({
				type: AlertType.ERROR,
				content: `Failed to save draft: ${msg}`,
			});
		},
	});

	const deleteDraft = useMutation({
		mutationFn: (draftId: string) =>
			Api.DeleteMetaAdsDraft(draftId).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.Drafts(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Draft deleted." });
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to delete draft." });
		},
	});

	const publishDraft = useMutation({
		mutationFn: (draftId: string) =>
			Api.PublishMetaAdsCampaign(draftId).then((r) => r.data),
		onSuccess: (result) => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.Drafts(company.id),
			});
			emit({
				type: AlertType.SUCCESS,
				content: `Campaign submitted to Meta. Status: ${result.effectiveStatus}.`,
			});
		},
		onError: (error) => {
			const msg = parseError(error) ?? "Unknown error";
			emit({
				type: AlertType.ERROR,
				content: `Failed to publish campaign: ${msg}`,
			});
		},
	});

	return { saveDraft, deleteDraft, publishDraft };
};
